"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import { SmartImage } from "@/components/SmartImage";
import { unsplash } from "@/lib/unsplash";
import { trackLead } from "@/lib/analytics";

const DISMISS_KEY = "onomac-lead-popup-dismissed";
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 gün

/**
 * Exit-intent lead popup — ilk ziyarette %10 karşılığı bülten kaydı.
 * Desktop: fare üstten çıkınca. Mobil: 30 sn zamanlayıcı. KVKK onaylı.
 */
export function LeadPopup() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [kvkk, setKvkk] = useState(false);
  const [status, setStatus] = useState<"idle" | "loading" | "done" | "error">("idle");
  const [message, setMessage] = useState("");

  useEffect(() => {
    let dismissedAt = 0;
    try {
      dismissedAt = Number(localStorage.getItem(DISMISS_KEY) ?? 0);
    } catch {
      return;
    }
    if (Date.now() - dismissedAt < DISMISS_TTL_MS) return;

    const show = () => {
      try {
        if (Number(localStorage.getItem(DISMISS_KEY) ?? 0)) return;
      } catch {}
      setOpen(true);
    };

    const isDesktop = window.matchMedia("(pointer: fine)").matches;
    let timer: ReturnType<typeof setTimeout> | null = null;

    if (isDesktop) {
      const onLeave = (e: MouseEvent) => {
        if (e.clientY <= 0) {
          show();
          document.removeEventListener("mouseout", onLeave);
        }
      };
      document.addEventListener("mouseout", onLeave);
      return () => document.removeEventListener("mouseout", onLeave);
    }

    timer = setTimeout(show, 30_000);
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        try {
          localStorage.setItem(DISMISS_KEY, String(Date.now()));
        } catch {}
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open ]);

  const close = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {}
    setOpen(false);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!kvkk) {
      setStatus("error");
      setMessage("Devam etmek için KVKK metnini onaylayın");
      return;
    }
    setStatus("loading");
    setMessage("");
    try {
      const res = await fetch("/api/newsletter", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, kvkk }),
      });
      const data = (await res.json()) as { success: boolean; error?: string };
      if (!res.ok || !data.success) {
        setStatus("error");
        setMessage(data.error ?? "Bir hata oluştu");
        return;
      }
      setStatus("done");
      trackLead("exit-popup");
      setMessage("Harika! %10 kodun e-postanda. Hoş geldin.");
      setTimeout(close, 2500);
    } catch {
      setStatus("error");
      setMessage("Bağlantı hatası");
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label="İndirim fırsatı">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={close} aria-hidden="true" />
      <div className="relative grid max-h-[90vh] w-full max-w-[960px] overflow-y-auto rounded-3xl bg-White shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)] md:grid-cols-2">
        <button
          onClick={close}
          aria-label="Kapat"
          className="absolute right-4 top-4 z-10 flex h-12 w-12 items-center justify-center rounded-full border-2 border-white/50 bg-white/20 text-White backdrop-blur-md transition hover:bg-white/30 active:scale-95"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="h-6 w-6" aria-hidden="true">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        <div className="flex flex-col items-center justify-center gap-6 rounded-3xl bg-Primary px-6 py-10 sm:px-14 md:rounded-l-3xl md:rounded-r-none">
          <span className="inline-flex items-center rounded-2xl bg-White px-4 py-2">
            <Image
              src="/images/onomac.svg"
              alt="ONOMAC"
              width={224}
              height={36}
              className="h-9 w-auto max-w-[224px] object-contain"
            />
          </span>
          <h2 className="text-center text-3xl font-medium leading-[1.25] text-White sm:text-4xl sm:leading-[48px]">
            İlk Siparişine Özel<br />%10 İndirim Kazan!
          </h2>
          <form onSubmit={submit} className="flex w-full flex-col gap-4">
            <div className="flex h-12 w-full items-center gap-2 rounded-2xl px-4 py-3 outline outline-1 outline-offset-[-1px] outline-White transition focus-within:outline-2">
              <label htmlFor="lead-email" className="sr-only">E-posta adresin</label>
              <input
                id="lead-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="E-mail"
                autoComplete="email"
                inputMode="email"
                disabled={status === "loading"}
                className="w-full min-w-0 flex-1 bg-transparent text-sm font-normal leading-4 text-White placeholder:text-White/70 focus:outline-none disabled:opacity-60"
              />
            </div>
            <label className="flex cursor-pointer items-start gap-2">
              <input
                type="checkbox"
                checked={kvkk}
                onChange={(e) => setKvkk(e.target.checked)}
                className="mt-0.5 h-4 w-4 shrink-0 accent-white"
              />
              <span className="text-xs leading-5 text-White/80">
                <a href="/kvkk" onClick={(e) => e.stopPropagation()} className="underline hover:text-White">KVKK metnini</a> okudum, bülten için onaylıyorum.
              </span>
            </label>
            <button
              type="submit"
              disabled={status === "loading"}
              className="flex h-12 w-full items-center justify-center gap-2 rounded-full bg-white px-6 py-3 text-base font-medium uppercase leading-6 text-Primary transition hover:bg-Background-light active:scale-[0.98] disabled:opacity-60"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="h-6 w-6 text-Primary-dark" aria-hidden="true">
                <path d="m22 2-7 20-4-9-9-4z" />
                <path d="M22 2 11 13" />
              </svg>
              {status === "loading" ? "Gönderiliyor..." : status === "done" ? "Kaydedildi" : "İndirim Kuponunu Gönder!"}
            </button>
            {message && <p role="status" className={`text-center text-xs ${status === "error" ? "text-White" : "text-emerald-200"}`}>{message}</p>}
          </form>
          <p className="max-w-96 text-center text-xs font-normal leading-5 text-White">
            Kampanya ve yeni ürünlerden ilk sen haberdar ol<br />İstediğin an çıkabilirsin.
          </p>
          <button type="button" onClick={close} className="text-center text-xs text-White/60 underline-offset-2 hover:text-White hover:underline">
            İstemiyorum, kaçırayım
          </button>
        </div>

        <div className="relative hidden min-h-[480px] items-center justify-center overflow-hidden rounded-b-3xl bg-rose-300 md:flex md:rounded-r-3xl md:rounded-bl-none">
          <SmartImage
           src="/images/collections/uno-color-gel.png"
            fallback={unsplash.cosmetics(482, 598)}
            alt=""
            fill
            className="object-cover"
            sizes="480px"
          />
        </div>
      </div>
    </div>
  );
}
